import { WarningFilled } from '@ant-design/icons'
import { TraceKeyResponseTypeEnum } from '@ulysses-inc/harami_api_client'
import type { GetProps } from 'antd'
import { Tooltip } from 'antd'
import { addMonths, format, subMonths } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { DateFnsDatePicker } from 'src/components/datepicker/DateFnsDatePicker'
import { semanticColors } from 'src/features/theme/semanticColors'
import { ReportListFilters } from './types'
import {
  getDefaultDateRange,
  useReportListFilters,
} from './useReportListFilters'

const { RangePicker } = DateFnsDatePicker
type RangePickerProps = GetProps<typeof RangePicker>
type DateRange = RangePickerProps['value']

type Props = {
  activeFilters: ReportListFilters
  executeSearch: ReturnType<typeof useReportListFilters>['executeSearch']
}

/**
 * 3ヶ月の上限を設定するdisabledDate関数
 */
const disabledDate: RangePickerProps['disabledDate'] = (
  current: Date,
  { from },
) => {
  if (from) {
    const maxDate = addMonths(from, 3)
    const minDate = subMonths(from, 3)
    return current < minDate || current > maxDate
  }
  return false
}

/**
 * デフォルトの日付範囲を取得（直近1ヶ月）
 */
const getDefaultDateRangeAsDate = (today: Date = new Date()): [Date, Date] => {
  const end = today
  const start = subMonths(end, 1)
  return [start, end]
}

/**
 * レポート一覧の実施日フィルター（SubHeader内に配置）
 *
 * - 直近1ヶ月がデフォルト値
 * - Max3ヶ月の上限
 * - 日付変更時に即座に検索実行
 * - ✖️ボタンで初期値（直近1ヶ月）に戻る
 */
export const ReportListDateRangeFilter = ({
  activeFilters,
  executeSearch,
}: Props) => {
  const [dateRange, setDateRange] = useState<DateRange>(
    getDefaultDateRangeAsDate,
  )

  // 日付形式の検索キーがある場合はdisabledにする
  const hasDateTraceKeyFilter = useMemo(() => {
    return activeFilters.traceKeyConditions.some(
      filter =>
        filter.responseType === TraceKeyResponseTypeEnum.Date &&
        filter.datetime?.greaterThanOrEqual &&
        filter.datetime?.lessThan,
    )
  }, [activeFilters.traceKeyConditions])

  // URLから日付が変更された場合にローカル状態を同期
  useEffect(() => {
    if (!activeFilters.dateFrom || !activeFilters.dateTo) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- URL状態とローカル状態の同期のため必要
      setDateRange(null)
      return
    }
    try {
      const from = new Date(activeFilters.dateFrom)
      const to = new Date(activeFilters.dateTo)
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
        setDateRange([from, to])
      }
    } catch {
      setDateRange(getDefaultDateRangeAsDate())
    }
  }, [activeFilters.dateFrom, activeFilters.dateTo])

  // 日付がデフォルト値と異なるかどうか
  const isDateChanged = useMemo(() => {
    const defaultRange = getDefaultDateRange()
    return (
      activeFilters.dateFrom !== defaultRange.dateFrom ||
      activeFilters.dateTo !== defaultRange.dateTo
    )
  }, [activeFilters.dateFrom, activeFilters.dateTo])

  const handleDateRangeChange = (dates: DateRange) => {
    if (!dates) {
      // クリアボタンが押された場合、デフォルト値（直近1ヶ月）に戻す
      const [start, end] = getDefaultDateRangeAsDate()
      setDateRange([start, end])
      const defaultRange = getDefaultDateRange()
      const updatedFilters: ReportListFilters = {
        ...activeFilters,
        dateFrom: defaultRange.dateFrom,
        dateTo: defaultRange.dateTo,
        page: 1,
      }
      executeSearch(updatedFilters)
      return
    }

    setDateRange(dates)
    if (dates[0] && dates[1]) {
      const dateFromStr = format(dates[0], 'yyyy-MM-dd')
      const dateToStr = format(dates[1], 'yyyy-MM-dd')

      const updatedFilters: ReportListFilters = {
        ...activeFilters,
        dateFrom: dateFromStr,
        dateTo: dateToStr,
        page: 1,
      }
      executeSearch(updatedFilters)
    }
  }

  return (
    <>
      <RangePicker
        value={dateRange}
        style={
          isDateChanged && !hasDateTraceKeyFilter
            ? {
                background: semanticColors.controlItemBgActive,
                borderColor: semanticColors.colorPrimaryBorderHover,
              }
            : undefined
        }
        onChange={handleDateRangeChange}
        inputReadOnly={true}
        disabledDate={disabledDate}
        disabled={hasDateTraceKeyFilter}
        placeholder={['開始日', '終了日']}
        allowEmpty={[true, true]}
        allowClear={isDateChanged}
      />
      {hasDateTraceKeyFilter && (
        <Tooltip title="種別が日時の検索キーを指定している場合は期間の絞り込みは無効になります">
          <WarningFilled
            style={{ color: semanticColors.colorWarning, marginLeft: 8 }}
          />
        </Tooltip>
      )}
    </>
  )
}
